import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Select from 'react-select';
import Sortable from 'sortablejs';
import axios from 'axios';
import * as Icons from 'react-feather';
import DataTable from 'react-data-table-component';
import { Link } from 'react-router-dom';
import { appendApplicationId, getApiBaseUrl } from '../../../utils/apiConfig';
import { getStorageValues } from '../../../shared/CommonFunctions';
import FileUploadModal from '../../../shared/file-upload/FileUploadModal';
import SKAlerts, { CreateAlert } from '../../../shared/common-alerts/SKAlerts';
import { ReactComponent as SortArrow } from '../../../shared/img/chevron-up-down.svg';
import { buildImageUrlFromUpload, resolveProductImageDisplayUrl } from './utils/productHelpers';

const BASE_IMAGE_TYPE_OPTIONS = [
  { value: 'default', label: 'Default' },
  { value: 'hover', label: 'Hover' },
  { value: 'gallery', label: 'Gallery' },
];

const PROMO_IMAGE_TYPE_OPTION = { value: 'promo', label: 'Promo Banner' };

const formatImageTypeLabel = (imageType) => {
  if (String(imageType || '').toLowerCase() === 'promo') {
    return 'Promo Banner';
  }
  return imageType || '-';
};

const sortImageRows = (rows = []) =>
  [...rows].sort(
    (a, b) =>
      Number(a.DisplayOrder || 0) - Number(b.DisplayOrder || 0) ||
      Number(a.ImageId || 0) - Number(b.ImageId || 0),
  );

const getNextDisplayOrder = (rows = []) => {
  if (!rows.length) return 1;
  return Math.max(...rows.map((row) => Number(row.DisplayOrder) || 0)) + 1;
};

function ProductImagesTab({
  productId,
  images = [],
  isPromoBanner = false,
  imageMode = 'product',
  readOnly = false,
  onRefresh,
}) {
  const isPromoMode = imageMode === 'promo';
  const defaultImageType = isPromoMode ? 'promo' : 'gallery';
  const matchesImageMode = (image) => {
    const type = String(image?.ImageType || '').toLowerCase();
    return isPromoMode ? type === 'promo' : type !== 'promo';
  };
  const [alert, setAlert] = useState(null);
  const [showUpload, setShowUpload] = useState(false);
  const [imageRows, setImageRows] = useState(() => sortImageRows(images));
  const [orderSaving, setOrderSaving] = useState(false);
  const [draft, setDraft] = useState({
    ImageId: null,
    ImageUrl: '',
    ImageType: defaultImageType,
  });
  const tableWrapperRef = useRef(null);
  const sortableRef = useRef(null);
  const imageRowsRef = useRef(imageRows);

  useEffect(() => {
    imageRowsRef.current = imageRows;
  }, [imageRows]);

  useEffect(() => {
    if (orderSaving) return;
    setImageRows(sortImageRows((images || []).filter(matchesImageMode)));
  }, [images, orderSaving, imageMode]);

  const resetDraft = () => {
    setDraft({ ImageId: null, ImageUrl: '', ImageType: defaultImageType });
  };

  const imageTypeOptions = useMemo(
    () => (isPromoMode ? [PROMO_IMAGE_TYPE_OPTION] : BASE_IMAGE_TYPE_OPTIONS),
    [isPromoMode],
  );

  useEffect(() => {
    if (!isPromoMode && draft.ImageType === 'promo') {
      setDraft((previous) => ({ ...previous, ImageType: 'gallery' }));
    }
    if (isPromoMode && draft.ImageType !== 'promo') {
      setDraft((previous) => ({ ...previous, ImageType: 'promo' }));
    }
  }, [isPromoMode, draft.ImageType]);

  const selectedImageType = useMemo(
    () => imageTypeOptions.find((option) => option.value === draft.ImageType) || imageTypeOptions[2] || imageTypeOptions[0],
    [draft.ImageType, imageTypeOptions],
  );

  const rowOrderKey = useMemo(
    () => imageRows.map((row) => row.ImageId).join(','),
    [imageRows],
  );

  const persistDisplayOrder = useCallback(async (orderedRows, previousRows) => {
    setOrderSaving(true);
    try {
      await axios.post(`${getApiBaseUrl()}/ec-product/image/update-order`, {
        ProductId: productId,
        OrderList: orderedRows.map((row, index) => ({
          ImageId: row.ImageId,
          DisplayOrder: index + 1,
        })),
        UserId: getStorageValues('userId'),
      });
      CreateAlert('Image order updated', 'Success', setAlert);
      if (onRefresh) onRefresh();
    } catch (err) {
      console.error(err);
      setImageRows(previousRows);
      CreateAlert('Unable to update image order', 'Error', setAlert);
    } finally {
      setOrderSaving(false);
    }
  }, [productId, onRefresh]);

  useEffect(() => {
    const wrapper = tableWrapperRef.current;
    const container = wrapper?.querySelector('.rdt_TableBody');
    if (!container || readOnly) return;

    if (sortableRef.current) {
      sortableRef.current.destroy();
      sortableRef.current = null;
    }

    sortableRef.current = Sortable.create(container, {
      handle: '.drag-handle',
      draggable: '.rdt_TableRow',
      animation: 150,
      scroll: true,
      disabled: orderSaving,
      onEnd: (evt) => {
        if (orderSaving || evt.oldIndex === evt.newIndex) return;

        const previousRows = imageRowsRef.current;
        const updated = [...previousRows];
        const [moved] = updated.splice(evt.oldIndex, 1);
        updated.splice(evt.newIndex, 0, moved);
        const withOrder = updated.map((row, index) => ({
          ...row,
          DisplayOrder: index + 1,
        }));

        setImageRows(withOrder);
        persistDisplayOrder(withOrder, previousRows);
      },
    });

    return () => {
      if (sortableRef.current) {
        sortableRef.current.destroy();
        sortableRef.current = null;
      }
    };
  }, [readOnly, orderSaving, rowOrderKey, imageRows.length, persistDisplayOrder]);

  const saveImage = async () => {
    if (!productId) {
      CreateAlert('Save product details first', 'Warning', setAlert);
      return;
    }
    if (!draft.ImageUrl?.trim()) {
      CreateAlert('Image URL is required', 'Warning', setAlert);
      return;
    }
    if (draft.ImageType === 'promo' && !isPromoBanner) {
      CreateAlert('Enable the promo banner option above first', 'Warning', setAlert);
      return;
    }
    if (!isPromoMode && draft.ImageType === 'promo') {
      CreateAlert('Promo banner images are managed in Promo Banners', 'Warning', setAlert);
      return;
    }

    let displayOrder = getNextDisplayOrder(imageRows);
    if (draft.ImageId) {
      const existing = imageRows.find((row) => row.ImageId === draft.ImageId);
      displayOrder = existing?.DisplayOrder ?? displayOrder;
    }

    try {
      const response = await axios.post(`${getApiBaseUrl()}/ec-product/image/save`, {
        ImageId: draft.ImageId,
        ProductId: productId,
        ImageUrl: draft.ImageUrl.trim(),
        ImageType: draft.ImageType,
        DisplayOrder: displayOrder,
        UserId: getStorageValues('userId'),
      });
      const result = response.data?.data;
      if (result?.success === 1) {
        CreateAlert(result.message || 'Image saved', 'Success', setAlert);
        resetDraft();
        if (onRefresh) onRefresh();
      } else {
        CreateAlert(result?.message || 'Failed to save image', 'Error', setAlert);
      }
    } catch (err) {
      console.error(err);
      CreateAlert('Failed to save image', 'Error', setAlert);
    }
  };

  const deleteImage = useCallback(async (imageId) => {
    try {
      const response = await axios.post(`${getApiBaseUrl()}/ec-product/image/delete`, {
        ImageId: imageId,
        UserId: getStorageValues('userId'),
      });
      const result = response.data?.data;
      if (result?.success === 1) {
        CreateAlert(result.message || 'Image deleted', 'Success', setAlert);
        if (onRefresh) onRefresh();
      }
    } catch (err) {
      console.error(err);
      CreateAlert('Failed to delete image', 'Error', setAlert);
    }
  }, [onRefresh]);

  const editRow = useCallback((row) => {
    setDraft({
      ImageId: row.ImageId,
      ImageUrl: row.ImageUrl || '',
      ImageType: row.ImageType || 'gallery',
    });
  }, []);

  const columns = useMemo(() => {
    const cols = [];

    if (!readOnly) {
      cols.push({
        name: <div className="titlename" />,
        cell: () => (
          <span className="drag-handle" style={{ cursor: orderSaving ? 'not-allowed' : 'grab' }}>
            <Icons.Menu size={16} />
          </span>
        ),
        width: '30px',
        sortable: false,
      });
    }

    cols.push(
      {
        name: (
          <div className="titlename">
            <span>Preview</span>
          </div>
        ),
        cell: (row) =>
          row.ImageUrl ? (
            <img
              src={resolveProductImageDisplayUrl(row.ImageUrl)}
              alt=""
              style={{ maxHeight: 48, maxWidth: 80 }}
            />
          ) : (
            '-'
          ),
        width: '100px',
        sortable: false,
      },
      {
        name: (
          <div className="titlename">
            <span>Type</span>
          </div>
        ),
        cell: (row) => formatImageTypeLabel(row.ImageType),
        sortable: false,
        sortIcon: <SortArrow />,
      },
    );

    if (!readOnly) {
      cols.push({
        name: <div className="titlename">Actions</div>,
        cell: (row) => (
          <div className="edit-delete-action">
            <Link
              className="me-2 p-2"
              to=""
              onClick={(e) => {
                e.preventDefault();
                editRow(row);
              }}
            >
              <Icons.Edit className="feather-edit" />
            </Link>
            <Link
              className="confirm-text p-2"
              to=""
              onClick={(e) => {
                e.preventDefault();
                deleteImage(row.ImageId);
              }}
            >
              <Icons.Trash2 className="feather-trash-2" />
            </Link>
          </div>
        ),
        center: true,
        width: '120px',
        sortable: false,
      });
    }

    return cols;
  }, [readOnly, orderSaving, editRow, deleteImage]);

  if (!productId) {
    return (
      <div className="alert alert-info mb-0">
        Save the product details first to upload images.
      </div>
    );
  }

  return (
    <>
      <SKAlerts alert={alert} />
      {!readOnly && (
        <div className="card mb-3 sk-product-image-form">
          <div className="card-body">
            {isPromoMode && !isPromoBanner ? (
              <p className="text-muted mb-0">Enable the promo banner option above to upload a promo image.</p>
            ) : (
              <>
                <h6 className="mb-2">{draft.ImageId ? 'Edit Image' : 'Add Image'}</h6>
                <div className="row">
              <div className="col-lg-5">
                <label className="form-label">Image URL</label>
                <div className="input-group">
                  <input
                    type="text"
                    className="form-control"
                    value={draft.ImageUrl}
                    onChange={(e) => setDraft((p) => ({ ...p, ImageUrl: e.target.value }))}
                    placeholder="Select single or multiple images"
                  />
                  <button
                    type="button"
                    className="btn btn-outline-primary d-flex align-items-center justify-content-center"
                    onClick={() => setShowUpload(true)}
                    title="Upload images"
                    aria-label="Upload images"
                  >
                    <Icons.Upload size={18} />
                  </button>
                </div>
                {draft.ImageUrl ? (
                  <img
                    src={resolveProductImageDisplayUrl(draft.ImageUrl)}
                    alt=""
                    className="mt-2 border rounded"
                    style={{ maxHeight: 80, maxWidth: 120 }}
                  />
                ) : null}
              </div>
              <div className="col-lg-3">
                <label className="form-label">Image Type</label>
                <Select
                  classNamePrefix="sk-product-image-type"
                  options={imageTypeOptions}
                  value={selectedImageType}
                  onChange={(option) => setDraft((p) => ({ ...p, ImageType: option?.value || defaultImageType }))}
                  isSearchable={false}
                  isDisabled={isPromoMode}
                />
              </div>
              <div className="col-lg-4 d-flex align-items-end gap-2">
                <button type="button" className="btn btn-submit" onClick={saveImage}>
                  {draft.ImageId ? 'Save' : 'Add'}
                </button>
                {draft.ImageId ? (
                  <button type="button" className="btn btn-cancel" onClick={resetDraft}>Cancel</button>
                ) : null}
              </div>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      <div className="card table-list-card sk-product-images-table" ref={tableWrapperRef}>
        <div className="card-body">
          <DataTable
            className="table-responsive product-list"
            columns={columns}
            data={imageRows}
            keyField="ImageId"
            pagination={false}
            fixedHeader
            fixedHeaderScrollHeight="320px"
            sortIcon={<SortArrow />}
            persistTableHead
            noDataComponent={<span className="text-muted">No images uploaded.</span>}
          />
        </div>
      </div>

      <FileUploadModal
        show={showUpload}
        onClose={() => setShowUpload(false)}
        categories={['product-images']}
        onConfirm={async ({ files }) => {
          if (!files?.length) return;

          try {
            const formDataToSend = new FormData();
            formDataToSend.append('entityId', productId);
            formDataToSend.append('entityType', 'ec_product');
            formDataToSend.append('fileCategory', 'product-images');
            formDataToSend.append('UserId', getStorageValues('userId'));
            files.forEach((file) => formDataToSend.append('files', file, file.name));
            appendApplicationId(formDataToSend);

            const uploadRes = await axios.post(`${getApiBaseUrl()}/file/upload`, formDataToSend, {
              headers: { 'Content-Type': 'multipart/form-data' },
            });
            const uploadedList = uploadRes.data?.data || [];

            if (!uploadedList.length) {
              CreateAlert('No files were uploaded', 'Warning', setAlert);
              return;
            }

            if (draft.ImageId) {
              const uploaded = uploadedList[0];
              if (uploaded?.FilePath) {
                setDraft((p) => ({
                  ...p,
                  ImageUrl: buildImageUrlFromUpload(uploaded.FilePath),
                }));
                CreateAlert('Image URL updated from upload', 'Success', setAlert);
              }
              return;
            }

            let nextOrder = getNextDisplayOrder(imageRows);
            let savedCount = 0;

            for (const uploaded of uploadedList) {
              if (!uploaded?.FilePath) continue;

              const response = await axios.post(`${getApiBaseUrl()}/ec-product/image/save`, {
                ImageId: null,
                ProductId: productId,
                ImageUrl: buildImageUrlFromUpload(uploaded.FilePath).trim(),
                ImageType: draft.ImageType,
                DisplayOrder: nextOrder,
                UserId: getStorageValues('userId'),
              });

              const result = response.data?.data;
              if (result?.success === 1) {
                savedCount += 1;
                nextOrder += 1;
              }
            }

            if (savedCount > 0) {
              CreateAlert(
                savedCount === 1 ? '1 image added' : `${savedCount} images added`,
                'Success',
                setAlert,
              );
              resetDraft();
              if (onRefresh) onRefresh();
            } else {
              CreateAlert('Files uploaded but could not be saved', 'Error', setAlert);
            }
          } catch (err) {
            console.error(err);
            CreateAlert('Failed to upload images', 'Error', setAlert);
          }
        }}
      />
    </>
  );
}

export default ProductImagesTab;
